import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import {
  AutoResponder,
  AutoResponderType,
  CampaignAutoResponderTriggersList,
  EmailResponse,
  EmailTemplate,
  EmailTemplateType,
  Pager,
  SMSResponse,
  Response,
  getFunnelPathVariables,
  Campaign,
  CampaignAutoResponderTrigger,
} from '../../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { AlertService, AutoResponderService, EmailTemplateService } from '../../../_services';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CrudSaveComponent } from '../../../_directives';
import { config } from '../../../../config/config';
import { takeUntil } from 'rxjs/operators';
import { addDummyValues } from '../../../steps-library/step-util';

@Component({
  selector: 'autoresponder',
  templateUrl: './autoresponder.component.html',
  styleUrls: ['../../campaign-section.scss', './autoresponder.component.scss'],
})
export class AutoresponderComponent extends CrudSaveComponent implements OnChanges {
  @Input('campaign') campaign: Campaign;
  @Input('responder') responder: AutoResponder;
  @Output() closeAutoresponder = new EventEmitter<void>();
  @Output() closeAutoresponderAndFetch = new EventEmitter<void>();
  @Output() updateAutoresponderTriggers = new EventEmitter<CampaignAutoResponderTrigger[]>();
  responderTypes = [
    { value: AutoResponderType.SMS, label: 'SMS' },
    { value: AutoResponderType.Email, label: 'Email' },
  ];
  autoResponderTypes = AutoResponderType;
  selectedType: AutoResponderType = null;
  emailTemplates: EmailTemplate[] = [];
  emailTemplatesLoading: boolean = false;
  public triggerFilterSettings = {
    singleSelection: false,
    allowSearchFilter: false,
    enableCheckAll: true,
    selectAllText: 'All Triggers',
  };
  triggerTypeOptions = [];
  currentPreviewIndex = 0;
  variables = getFunnelPathVariables();
  isNew: boolean = false;
  showTriggerPrompt: boolean = false;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected responderService: AutoResponderService,
    protected alertService: AlertService,
    protected templateService: EmailTemplateService,
    protected formBuilder: FormBuilder
  ) {
    super(router, location, route, responderService, alertService);

    // Initialize trigger type options
    this.triggerTypeOptions = CampaignAutoResponderTriggersList.map((label, index) => ({
      id: index + 1, // AutoResponderTrigger enum values start from 1
      text: label,
    }));
  }

  ngOnInit() {
    this.setIsNewFlag();

    this.form = this.formBuilder.group({
      id: [null],
      name: [null, Validators.required],
      type: [AutoResponderType.SMS, Validators.required],
      responses: this.formBuilder.array([]),
      trigger_types: [null],
    });

    if (this.responder && this.responder.id) {
      this.form.patchValue(this.responder);
      this.mapTriggerTypes();
      this.responder = this.responder;
      this.selectedType = Number(this.responder.type);

      for (let response of this.responder.responses) {
        this.addResponse(response);
      }
    }

    super.ngOnInit();
    this.selectType(this.form.get('type').value);
    this.loadEmailTemplates();

    // Subscribe to form changes to monitor trigger_types
    this.form
      .get('trigger_types')
      .valueChanges.pipe(takeUntil(this._destroy$))
      .subscribe((triggerTypes) => {
        this.onTriggerTypesChange(triggerTypes);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.responder && changes.responder.currentValue) {
      this.setIsNewFlag();
      if (this.form) {
        this.form.patchValue(this.responder);
        this.mapTriggerTypes();
        this.selectedType = this.responder.type;
        this.responder = this.responder;
        this.loadEmailTemplates();
      }
    }
  }

  private mapTriggerTypes() {
    // Map trigger_types to the format expected by multiselect dropdown
    if (this.responder.trigger_types && this.responder.trigger_types.length > 0) {
      const mappedTriggerTypes = this.responder.trigger_types
        .map((triggerId) => this.triggerTypeOptions.find((option) => option.id === triggerId))
        .filter(Boolean);

      this.form.get('trigger_types').setValue(mappedTriggerTypes);
    }
  }

  private setIsNewFlag() {
    if (this.responder && this.responder.id) {
      this.isNew = false;
    } else {
      this.isNew = true;
    }
  }

  getTypeLabel(typeValue: any): string {
    const type = this.responderTypes.find((t) => t.value == typeValue);
    return type ? type.label : '';
  }

  private loadEmailTemplates() {
    this.emailTemplatesLoading = true;
    this.templateService
      .list({ page: 1, page_size: config.maxPageSize, type: EmailTemplateType.AutoResponder })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (pager: Pager) => {
          this.emailTemplatesLoading = false;
          this.emailTemplates = pager.results;
        },
        (error) => {
          this.emailTemplatesLoading = false;
          this.handleError(error);
        }
      );
  }

  protected setObjectNameFromType(type) {
    if (type == AutoResponderType.SMS) {
      this.objectName = 'SMS Autoresponder';
    } else if (type == AutoResponderType.Email) {
      this.objectName = 'Email Autoresponder';
    }

    this.title = this.isNew ? 'Add a New ' + this.objectName : 'Change Your ' + this.objectName;
  }

  selectType(autoresponderType) {
    // Convert string to number
    const type = Number(autoresponderType);
    this.form.get('type').setValue(type);

    if (this.isNew) {
      //only new autoresponders can change type
      if (this.selectedType != type) {
        this.selectedType = type;

        //clear the responses and create a single empty one
        const control = <FormArray>this.form.controls['responses'];

        for (let i = control.length - 1; i >= 0; i--) {
          control.removeAt(i);
        }

        control.push(this.initResponse());
      }
    }
  }

  private isProcessableNumber(n) {
    return n !== null && n !== '' && n !== undefined && !isNaN(Number(n));
  }

  private convertDelayToTime(delay) {
    let time = { days: 0, hours: 0, minutes: 0 };
    if (!this.isProcessableNumber(delay)) {
      return time;
    }
    delay = Number(delay);
    time['days'] = Math.floor(delay / 86400);
    let remainder = delay % 86400;
    time['hours'] = Math.floor(remainder / 3600);
    remainder = remainder % 3600;
    time['minutes'] = Math.floor(remainder / 60);
    return time;
  }

  private convertTimeToDelay(days, hours, minutes) {
    if (!this.isProcessableNumber(days) || !this.isProcessableNumber(hours) || !this.isProcessableNumber(minutes)) {
      return 0;
    }
    return Number(days) * 86400 + Number(hours) * 3600 + Number(minutes) * 60;
  }

  protected initResponse(response?: Response) {
    let group: FormGroup;

    // Custom validator to ensure at least one of days, hours, or minutes is > 0 and not null
    const atLeastOneTimeSet = (fg: FormGroup) => {
      const daysCtrl = fg.get('days');
      const hoursCtrl = fg.get('hours');
      const minutesCtrl = fg.get('minutes');
      const days = daysCtrl ? daysCtrl.value : null;
      const hours = hoursCtrl ? hoursCtrl.value : null;
      const minutes = minutesCtrl ? minutesCtrl.value : null;
      if ((days && Number(days) > 0) || (hours && Number(hours) > 0) || (minutes && Number(minutes) > 0)) {
        return null;
      }
      return { atLeastOneTimeRequired: true };
    };

    if (this.selectedType == AutoResponderType.SMS) {
      const smsResponse = <SMSResponse>response;
      let timeData = this.convertDelayToTime(smsResponse ? smsResponse.delay : null);
      group = this.formBuilder.group(
        {
          id: [smsResponse ? smsResponse.id : null],
          days: [timeData.days, [Validators.required, Validators.min(0)]],
          hours: [timeData.hours, [Validators.required, Validators.min(0)]],
          minutes: [timeData.minutes, [Validators.required, Validators.min(0)]],
          message: [smsResponse ? smsResponse.message : null, Validators.required],
          message_after_hours: [smsResponse ? smsResponse.message_after_hours : null, null],
        },
        { validators: atLeastOneTimeSet }
      );
    } else if (this.selectedType == AutoResponderType.Email) {
      const emailResponse = <EmailResponse>response;
      let timeData = this.convertDelayToTime(emailResponse ? emailResponse.delay : null);
      group = this.formBuilder.group(
        {
          id: [emailResponse ? emailResponse.id : null],
          days: [timeData.days, [Validators.required, Validators.min(0)]],
          hours: [timeData.hours, [Validators.required, Validators.min(0)]],
          minutes: [timeData.minutes, [Validators.required, Validators.min(0)]],
          template: [emailResponse ? emailResponse.template : null, Validators.required],
          template_after_hours: [emailResponse ? emailResponse.template_after_hours : null, null],
        },
        { validators: atLeastOneTimeSet }
      );
    }
    return group;
  }

  addResponse(response?: Response) {
    const control = <FormArray>this.form.controls['responses'];
    control.push(this.initResponse(response));
  }

  removeResponse(index: number) {
    // remove address from the list
    const control = <FormArray>this.form.controls['responses'];
    control.removeAt(index);
  }

  get responseControls() {
    return <FormArray>this.form.get('responses')['controls'];
  }

  emailTemplatesChanged() {
    this.loadEmailTemplates();
  }

  protected getFormData(): any {
    let value = super.getFormData();
    let responses = [];
    value.responses.forEach((response) => {
      response.delay = this.convertTimeToDelay(response.days, response.hours, response.minutes);
      responses.push(response);
    });
    value.responses = responses;
    return value;
  }

  onSubmit(): void {
    if (!this.isNew) {
      this.id = this.responder.id;
      this.updateTriggers();
    }
    super.onSubmit();
  }

  protected onSaveComplete(data: AutoResponder): void {
    if (this.isNew) {
      this.updateTriggers(data);
    }
    this.closeAutoresponderAndFetch.emit();
  }

  updateTriggers(autoresponder?: AutoResponder) {
    const autoresponderId = autoresponder ? autoresponder.id : this.id;
    const autoresponder_type = autoresponder ? autoresponder.type : this.responder.type;
    const triggers = this.form.get('trigger_types').value;

    // Create campaignTriggers array
    const campaignTriggers = [];
    if (triggers && triggers.length > 0) {
      triggers.forEach((trigger) => {
        campaignTriggers.push({
          campaign: this.campaign.id,
          autoresponder: autoresponderId,
          autoresponder_type,
          trigger_type: trigger.id,
        });
      });
    }

    // Send campaignTriggers to parent component
    this.updateAutoresponderTriggers.emit(campaignTriggers);
  }

  onTriggerTypesChange(triggerTypes: any[]) {
    this.showTriggerPrompt = false;

    if (this.campaign && this.campaign.autoresponder_triggers && triggerTypes && triggerTypes.length > 0) {
      for (const trigger of triggerTypes) {
        const matchExists = this.campaign.autoresponder_triggers.some(
          (existingTrigger) =>
            existingTrigger.trigger_type === trigger.id &&
            existingTrigger.autoresponder !== ((this.responder && this.responder.id) || null)
        );
        if (matchExists) {
          this.showTriggerPrompt = true;
          break;
        }
      }
    }
  }

  navBack() {
    this.closeAutoresponder.emit();
  }

  // Preview navigation methods
  previousResponse() {
    if (this.currentPreviewIndex > 0) {
      this.currentPreviewIndex--;
    }
  }

  nextResponse() {
    const responseCount = this.responseControls.length;
    if (this.currentPreviewIndex < responseCount - 1) {
      this.currentPreviewIndex++;
    }
  }

  getCurrentResponseMessage(): string {
    const responses = this.responseControls;
    if (responses && responses[this.currentPreviewIndex]) {
      const currentResponse = responses[this.currentPreviewIndex];
      let message;
      if (this.selectedType === AutoResponderType.SMS) {
        const messageControl = currentResponse.get('message');
        if (messageControl) {
          message = addDummyValues(messageControl.value, this.variables);
        }
      } else if (this.selectedType === AutoResponderType.Email) {
        const templateId = currentResponse.get('template') && currentResponse.get('template').value;
        let templateContent;
        if (templateId) {
          templateContent = this.emailTemplates.find((template) => template.id === templateId);
        }

        if (templateContent) {
          message = addDummyValues(templateContent.body, this.variables);
        }
      }
      return message;
    }
    return '';
  }

  getResponseTypeLabel(): string {
    return this.selectedType === AutoResponderType.SMS ? 'SMS' : 'Email';
  }
}
